import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

export default function Services() {
  const [active, setActive] = useState(SERVICES[0].id);
  const current = SERVICES.find((s) => s.id === active);

  return (
    <div data-testid="services-page">
      <PageHero
        overline="Our Services"
        lines={["Complete Agricultural", "Solutions Under", "One Roof"]}
        subtitle="From soilless systems to turnkey builds — pick a service to explore the full scope of what our agronomists and engineers deliver."
        testId="services-hero"
      />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          {/* TOC */}
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32 flex flex-col">
              {SERVICES.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={`service-tab-${s.id}`}
                  onClick={() => setActive(s.id)}
                  className={`group text-left py-5 border-t border-border flex items-center gap-4 transition-colors duration-300 ${
                    active === s.id ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <span className="font-mono text-xs">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-clash text-lg md:text-xl leading-tight flex-1">
                    {s.title}
                  </span>
                  <s.icon className="w-5 h-5 shrink-0" strokeWidth={1.6} />
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="md:col-span-7 md:col-start-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`service-detail-${active}`}
              >
                <current.icon className="w-12 h-12 text-primary" strokeWidth={1.4} />
                <h2 className="mt-8 font-clash text-3xl md:text-4xl tracking-tight leading-tight">
                  {current.title}
                </h2>
                <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                  {current.description}
                </p>
                <ul className="mt-10 border-t border-border">
                  {current.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-start gap-4 py-4 border-b border-border"
                    >
                      <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground/90">{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 p-6 bg-accent/40 border border-border">
                  <span className="overline text-primary">Ideal For</span>
                  <p className="mt-2 text-foreground/90">{current.ideal}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <h2 className="font-clash text-3xl md:text-5xl tracking-tight max-w-3xl">
            Not sure which service fits your land?
          </h2>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            Tell us about your plot, crop idea, or budget — we'll recommend the
            right path in a free consultation.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="group mt-10 overline inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-bold transition-transform duration-300 hover:scale-[1.04]"
          >
            Talk to an Agronomist
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
